/**
 * The plan's FIFTH source: the openAIP record behind a waypoint — "type nothing
 * the data already knows".
 *
 * Same shape as `route.ts`, `aircraft.ts` and `wx.ts`: everything above the
 * `--- the network step ---` divider is a pure mapping from an openAIP feature to
 * LBA field values, so the rules are pinned down by tests (against JSON captured
 * from the live API) instead of by clicking. The store owns *when* it runs and
 * which cells it may touch (`PlanStore.setGeo`, source `'geo'`); this file only
 * says what the values are.
 *
 * What it fills, and nothing else:
 *   · V2 Frequenzen — `fq_dep_*` / `fq_dest_*` (Rollkontrolle, Turm, INFO)
 *   · V7 Flugplatzdaten — `aip_p1_*` (Start), `aip_p2f_*` (Ziel), `aip_p20_*`
 *     (Ausweich): Bahnrichtung, Bahnlänge, Höhe, Belag, Grasbahn
 *   · B6 `atis1_station` / `atis2_station` — "LJLJ ATIS 128.175", the cell the
 *     paper form calls *Station/Frequenz*
 *   · the route block's per-checkpoint cells: `cp<N>_freq` (the VOR/NDB column)
 *     and `leg<N>_remark` (Bemerkungen/Frequenzen)
 *
 * Two decisions worth writing down:
 *
 * **The remark is written in the INTERFACE language, at write time.** A reporting
 * point becomes "Pflichtmeldepunkt" or "Compulsory reporting point" depending on
 * what the app is set to when the cell is filled — it is not re-translated later.
 * The alternative (store a code, translate at render) cannot work: the value is
 * printed onto the PDF and read on paper, where there is no renderer left. Every
 * other cell of the form is language-independent (numbers, ICAO codes, radio
 * abbreviations), so this is the only one where the question arises.
 *
 * **Frequencies are written exactly as openAIP publishes them.** "118.005" keeps
 * its three decimals — that is an 8.33 kHz channel, and trimming it to "118.01"
 * or "118.0" would name a different one.
 */

import * as api from './api';
import type { GeoFeature } from './api';
import { LEG_NUMBERS, legField } from './fields';
import type { T } from './i18n';
import { MAX_ROUTE_LEGS, type FeatureLayer, type Waypoint } from './route';
import type { PlanStore } from './store';

/* ================================================================== *
 * openAIP enums
 * ================================================================== */

/**
 * `frequencies[].type`. **Mirrors `data/openaip-kinds.json` → `frequencyType`**,
 * which carries the provenance: the enum is not in openAIP's OpenAPI spec and was
 * recovered from the mirror by clustering the published `name` per `type`. Change
 * both or neither.
 */
export type FreqKind =
  | 'approach' | 'apron' | 'arrival' | 'center' | 'ctaf' | 'delivery' | 'departure'
  | 'fis' | 'gliding' | 'ground' | 'info' | 'multicom' | 'unicom' | 'radar'
  | 'tower' | 'atis' | 'radio' | 'other' | 'airmet' | 'awos' | 'lights' | 'volmet' | 'afis';

const FREQ_KINDS: readonly FreqKind[] = [
  'approach', 'apron', 'arrival', 'center', 'ctaf', 'delivery', 'departure',
  'fis', 'gliding', 'ground', 'info', 'multicom', 'unicom', 'radar',
  'tower', 'atis', 'radio', 'other', 'airmet', 'awos', 'lights', 'volmet', 'afis',
];

/** An openAIP frequency type → its stable kind; anything unknown is `'other'`. */
export function freqKind(type: unknown): FreqKind {
  const n = typeof type === 'number' ? type : Number(type);
  if (!Number.isInteger(n)) return 'other';
  return FREQ_KINDS[n] ?? 'other';
}

/**
 * What the form writes in front of the number — the abbreviation a pilot says on
 * the radio, not a translation. AFIS, Info and FIS all print `INFO`: the LBA
 * form's own V2 row is called INFO, and a German pilot calling Győr-Pér says
 * "Per Information" whichever of the three openAIP filed it under.
 */
const FREQ_LABEL: Readonly<Record<FreqKind, string>> = {
  approach: 'APP', apron: 'APRON', arrival: 'ARR', center: 'ACC', ctaf: 'CTAF',
  delivery: 'DEL', departure: 'DEP', fis: 'INFO', gliding: 'SEGELFLUG', ground: 'GND',
  info: 'INFO', multicom: 'MULTICOM', unicom: 'UNICOM', radar: 'RADAR', tower: 'TWR',
  atis: 'ATIS', radio: 'RADIO', other: '', airmet: 'AIRMET', awos: 'AWOS',
  lights: 'LIGHTS', volmet: 'VOLMET', afis: 'INFO',
};

/**
 * `surface.mainComposite` → the value the Belag datalist offers (see PlanForm's
 * `belagOptions`). Everything else — brick, coral, ice, "unknown" — is **absent**
 * rather than guessed: the AIP prints what it prints, and an invented Belag on a
 * printed flight plan is worse than an empty cell.
 */
const BELAG: Readonly<Record<number, string>> = {
  0: 'ASPH',      // Asphalt
  1: 'Beton',     // Concrete
  2: 'Gras',      // Grass
  3: 'Sand',      // Sand
  5: 'ASPH',      // Bituminous tar or asphalt
  7: 'ASPH',      // Macadam / tarmac
  8: 'Schotter',  // Stone
  12: 'Schotter', // Gravel
};

/** `runwaySurfaceComposition` 2 — the one that ticks the Grasbahn column. */
const GRASS = 2;

/** openAIP `distanceUnit`: 0 metres, 1 feet. Runway dimensions are always 0 in the exports. */
const M_PER_FT = 0.3048;
export const FT_PER_M = 3.28084;

/* ================================================================== *
 * reading a feature
 * ================================================================== */

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const numOr = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** A `{value, unit}` distance in metres, whatever unit openAIP filed it in. */
function metres(v: unknown): number | null {
  if (!isObj(v)) return null;
  const n = numOr(v['value']);
  if (n === null) return null;
  return numOr(v['unit']) === 1 ? n * M_PER_FT : n;
}

export interface Freq {
  kind: FreqKind;
  /** the published number, verbatim: "118.005" stays "118.005" */
  value: string;
  /** openAIP `frequencyUnit`: 1 kHz, 2 MHz */
  unit: number;
  /** openAIP's own name, e.g. "PER INFORMATION" — kept for the info card, never printed by the feed */
  name: string;
  primary: boolean;
}

export interface Runway {
  designator: string;
  /** metres, rounded; null when openAIP publishes no length */
  lengthM: number | null;
  /** `surface.mainComposite`, or the first `composition` entry */
  surface: number | null;
  /** `operations` 2 — a closed runway is never the main one */
  closed: boolean;
  main: boolean;
}

/** One physical runway: the two ends as the AIP writes them ("10L/28R"). */
export interface RunwayPair {
  label: string;
  lengthM: number | null;
  surface: number | null;
  /** either end is openAIP's `mainRunway` — the tie-break between equally long runways */
  main: boolean;
}

export interface AirportFacts {
  /** ICAO code, or '' — small fields often have none */
  icao: string;
  name: string;
  /** feet AMSL, converted from openAIP's metres */
  elevFt: number | null;
  frequencies: Freq[];
  runways: Runway[];
}

/** `raw.frequencies[]`, typed. Non-public frequencies are dropped — they are not ours to call. */
export function frequencies(raw: Record<string, unknown> | null | undefined): Freq[] {
  const list = raw?.['frequencies'];
  if (!Array.isArray(list)) return [];
  const out: Freq[] = [];
  for (const f of list) {
    if (!isObj(f)) continue;
    if (f['publicUse'] === false) continue;
    const value = str(f['value']);
    if (!value) continue;
    out.push({
      kind: freqKind(f['type']),
      value,
      unit: numOr(f['unit']) ?? 2,
      name: str(f['name']),
      primary: f['primary'] === true,
    });
  }
  return out;
}

/** `raw.runways[]`, typed. openAIP lists every runway END separately. */
export function runways(raw: Record<string, unknown> | null | undefined): Runway[] {
  const list = raw?.['runways'];
  if (!Array.isArray(list)) return [];
  const out: Runway[] = [];
  for (const r of list) {
    if (!isObj(r)) continue;
    const designator = str(r['designator']).toUpperCase();
    if (!designator) continue;
    const dim = r['dimension'];
    const len = isObj(dim) ? metres(dim['length']) : null;
    const surf = r['surface'];
    let surface: number | null = null;
    if (isObj(surf)) {
      surface = numOr(surf['mainComposite']);
      if (surface === null && Array.isArray(surf['composition'])) surface = numOr(surf['composition'][0]);
    }
    out.push({
      designator,
      lengthM: len === null ? null : Math.round(len),
      surface,
      closed: numOr(r['operations']) === 2,
      main: r['mainRunway'] === true,
    });
  }
  return out;
}

/** Everything the feed reads off an airport feature. */
export function airportFacts(f: GeoFeature): AirportFacts {
  const raw = isObj(f.raw) ? f.raw : {};
  const elevM = metres(raw['elevation']);
  const propElev = isObj(f.props) ? numOr(f.props['elev_ft']) : null;
  return {
    icao: (str(raw['icaoCode']) || (isObj(f.props) ? str(f.props['icao']) : '')).toUpperCase(),
    name: str(f.name),
    elevFt: elevM === null ? propElev : Math.round(elevM * FT_PER_M),
    frequencies: frequencies(raw),
    runways: runways(raw),
  };
}

/* ================================================================== *
 * runway pairing
 * ================================================================== */

interface Desig {
  num: number;
  side: string;
}

/** "10L" → {num:10, side:'L'}; null for anything that is not a runway designator. */
function parseDesignator(d: string): Desig | null {
  const m = /^(\d{1,2})([LRC])?$/.exec(d.replace(/\s+/g, ''));
  if (!m) return null;
  const num = Number(m[1] ?? '');
  if (!Number.isInteger(num) || num < 1 || num > 36) return null;
  return { num, side: m[2] ?? '' };
}

const OPPOSITE: Readonly<Record<string, string>> = { L: 'R', R: 'L', C: 'C', '': '' };

/** Is `b` the other end of `a`? 10L ↔ 28R, 11 ↔ 29, 18 ↔ 36. */
function isReciprocal(a: Desig, b: Desig): boolean {
  return b.num === ((a.num + 17) % 36) + 1 && b.side === OPPOSITE[a.side];
}

/**
 * The physical runways of an airport, from openAIP's list of ENDS.
 *
 * Two ends belong together when their designators are reciprocal **and** their
 * published lengths agree. The length check is not decoration: Frankfurt-Egelsbach
 * publishes 08/26 twice — 1 400 m asphalt and 670 m grass — and pairing by
 * designator alone would produce a 1 400 m grass runway that exists nowhere.
 *
 * Closed ends (`operations` 2) are dropped first, the same rule the tiles use for
 * the runway-direction icon (`data/openaip-kinds.json` → `_runwayDirection`).
 */
export function runwayPairs(list: readonly Runway[]): RunwayPair[] {
  const open = list.filter(r => !r.closed);
  const used = new Set<number>();
  const out: RunwayPair[] = [];
  for (let i = 0; i < open.length; i++) {
    if (used.has(i)) continue;
    const a = open[i];
    if (!a) continue;
    used.add(i);
    const da = parseDesignator(a.designator);
    let partner: Runway | null = null;
    if (da) {
      for (let j = i + 1; j < open.length; j++) {
        if (used.has(j)) continue;
        const b = open[j];
        if (!b) continue;
        const db = parseDesignator(b.designator);
        if (!db || !isReciprocal(da, db)) continue;
        if (a.lengthM !== null && b.lengthM !== null && a.lengthM !== b.lengthM) continue;
        used.add(j);
        partner = b;
        break;
      }
    }
    const ends = partner ? [a, partner] : [a];
    // the AIP writes the lower designator first: "11/29", "10L/28R"
    const sorted = [...ends].sort(
      (x, y) => (parseDesignator(x.designator)?.num ?? 0) - (parseDesignator(y.designator)?.num ?? 0),
    );
    out.push({
      label: sorted.map(r => r.designator).join('/'),
      lengthM: Math.max(...ends.map(r => r.lengthM ?? 0)) || null,
      surface: a.surface ?? partner?.surface ?? null,
      main: ends.some(r => r.main),
    });
  }
  return out;
}

/**
 * The runway the form's V7 row is about: the LONGEST one. Where two are equally
 * long the one openAIP marks `mainRunway` wins, and failing that the first listed.
 */
export function mainRunwayPair(list: readonly Runway[]): RunwayPair | null {
  let best: RunwayPair | null = null;
  let bestScore = -Infinity;
  for (const p of runwayPairs(list)) {
    const score = (p.lengthM ?? 0) * 2 + (p.main ? 1 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = p;
    }
  }
  return best;
}

/** The Belag cell for a surface code, or '' when openAIP's code has no LBA option. */
export const belagFor = (surface: number | null | undefined): string =>
  surface == null ? '' : BELAG[surface] ?? '';

/** The Grasbahn column: the paper form carries an "x", nothing means no. */
export const grasFor = (surface: number | null | undefined): string => (surface === GRASS ? 'x' : '');

/* ================================================================== *
 * picking a frequency
 * ================================================================== */

/** V2 „Rollkontrolle“ — Ground, or the Apron frequency where a field has no Ground. */
export const GROUND_KINDS: readonly FreqKind[] = ['ground', 'apron'];
/** V2 „Turm“. */
export const TOWER_KINDS: readonly FreqKind[] = ['tower'];
/** V2 „INFO“ — AFIS, Information, FIS, CTAF, Radio, in that order. */
export const INFO_KINDS: readonly FreqKind[] = ['afis', 'info', 'fis', 'ctaf', 'radio'];
/** The single frequency a checkpoint line prints: the one you actually call. */
export const CALL_KINDS: readonly FreqKind[] = ['tower', 'afis', 'info', 'fis', 'ctaf', 'radio'];
/** B6 Station/Frequenz: the ATIS if there is one, otherwise whoever you call. */
export const STATION_KINDS: readonly FreqKind[] = ['atis', ...CALL_KINDS];

/**
 * The best frequency of the wanted kinds: earliest kind in the list wins, then a
 * `primary` one over a secondary, then whatever openAIP listed first. Ljubljana
 * files six frequencies and the Turm cell has room for one.
 */
export function pickFreq(list: readonly Freq[], kinds: readonly FreqKind[]): Freq | null {
  let best: Freq | null = null;
  let bestRank = Infinity;
  for (let i = 0; i < list.length; i++) {
    const f = list[i];
    if (!f) continue;
    const k = kinds.indexOf(f.kind);
    if (k < 0) continue;
    const rank = k * 1000 + (f.primary ? 0 : 500) + i;
    if (rank < bestRank) {
      bestRank = rank;
      best = f;
    }
  }
  return best;
}

/** The number as the form's frequency columns hold it; kHz (an NDB) says so. */
export const freqValue = (f: Freq): string => (f.unit === 1 ? f.value + ' kHz' : f.value);

/**
 * One printable radio line: `LJLJ TWR 118.005`, `LHPR INFO 129.910`.
 * The station prefix is the ICAO code, or the published name when a field has none.
 */
export function freqLine(station: string, f: Freq | null): string {
  if (!f) return '';
  const label = FREQ_LABEL[f.kind];
  return [station, label, freqValue(f)].filter(Boolean).join(' ');
}

/* ================================================================== *
 * the mapping into the form
 * ================================================================== */

/** The three V7 rows: Start, Ziel, Ausweich. */
export type AipSlot = 'p1' | 'p2f' | 'p20';
export const AIP_SLOTS: readonly AipSlot[] = ['p1', 'p2f', 'p20'];

/** The V2 frequency block only has a departure and a destination column. */
const FQ_WHERE: Readonly<Partial<Record<AipSlot, string>>> = { p1: 'dep', p2f: 'dest' };
/** B6 has exactly two rows, and they belong to the departure and the destination. */
const ATIS_ROW: Readonly<Partial<Record<AipSlot, number>>> = { p1: 1, p2f: 2 };

/**
 * Everything one aerodrome writes into its slot. A value openAIP does not know is
 * **absent** from the map, never an empty string, so `geoValues` can tell "nothing
 * to say" from "this cell is not mine".
 */
export function airportValues(f: GeoFeature, slot: AipSlot): Map<string, string> {
  const out = new Map<string, string>();
  const facts = airportFacts(f);
  const station = facts.icao || facts.name;

  const where = FQ_WHERE[slot];
  if (where) {
    const put = (key: string, freq: Freq | null) => {
      if (freq) out.set('fq_' + where + '_' + key, freqValue(freq));
    };
    put('ground', pickFreq(facts.frequencies, GROUND_KINDS));
    put('twr', pickFreq(facts.frequencies, TOWER_KINDS));
    put('info', pickFreq(facts.frequencies, INFO_KINDS));
  }

  const row = ATIS_ROW[slot];
  if (row !== undefined) {
    const line = freqLine(station, pickFreq(facts.frequencies, STATION_KINDS));
    if (line) out.set('atis' + row + '_station', line);
  }

  const rwy = mainRunwayPair(facts.runways);
  const aip = (key: string, v: string) => {
    if (v) out.set('aip_' + slot + '_' + key, v);
  };
  if (rwy) {
    aip('rwy', rwy.label);
    if (rwy.lengthM !== null) aip('len', String(rwy.lengthM));
    aip('belag', belagFor(rwy.surface));
    aip('gras', grasFor(rwy.surface));
  }
  if (facts.elevFt !== null) aip('elev', String(facts.elevFt));
  return out;
}

/**
 * The line the Bemerkungen/Frequenzen column gets for one checkpoint, and the
 * number its VOR/NDB column gets. See the module head on the language question.
 */
export interface CheckpointLine {
  remark: string;
  freq: string;
}

export function checkpointLine(f: GeoFeature | null | undefined, t: T): CheckpointLine {
  if (!f) return { remark: '', freq: '' };
  if (f.layer === 'airports') {
    const facts = airportFacts(f);
    const best = pickFreq(facts.frequencies, CALL_KINDS);
    return { remark: freqLine(facts.icao || facts.name, best), freq: best ? freqValue(best) : '' };
  }
  if (f.layer === 'navaids') {
    const raw = isObj(f.raw) ? f.raw : {};
    const props = isObj(f.props) ? f.props : {};
    // `props.type` / `props.ident` are the server's decoded columns (openAIP's
    // navaidType enum lives in `data/openaip-kinds.json` and is applied on ingest),
    // so the client does not carry a second copy of that table.
    const type = (str(props['type']) || f.kind).toUpperCase();
    const ident = (str(props['ident']) || str(raw['identifier']) || f.name).toUpperCase();
    const fr = isObj(raw['frequency']) ? raw['frequency'] : null;
    const value = fr ? str(fr['value']) : '';
    const unit = fr ? numOr(fr['unit']) ?? 2 : 2;
    const shown = value ? (unit === 1 ? value + ' kHz' : value) : '';
    return { remark: [type, ident, shown].filter(Boolean).join(' '), freq: value };
  }
  if (f.layer === 'reporting_points') {
    const props = isObj(f.props) ? f.props : {};
    const raw = isObj(f.raw) ? f.raw : {};
    const compulsory = props['compulsory'] === true || raw['compulsory'] === true;
    return { remark: t(compulsory ? 'geo.rp.compulsory' : 'geo.rp.optional'), freq: '' };
  }
  return { remark: '', freq: '' };
}

/** The checkpoint rows the paper form has: `cp1` (Start) … `cp13`. */
export const CP_ROWS: readonly number[] = Array.from({ length: MAX_ROUTE_LEGS + 1 }, (_, i) => i + 1);

/**
 * Every field id the `'geo'` source may own.
 *
 * It is the *domain*, not the current answer: a cell in here that the current
 * route has nothing to say about is CLEARED when it still carries a geo tag —
 * that is how a deleted waypoint takes its remark with it, exactly as the route
 * sync takes back a cp name.
 */
export const GEO_IDS: readonly string[] = (() => {
  const ids: string[] = [];
  for (const slot of AIP_SLOTS) {
    for (const key of ['rwy', 'len', 'elev', 'belag', 'gras']) ids.push('aip_' + slot + '_' + key);
    const where = FQ_WHERE[slot];
    if (where) for (const key of ['ground', 'twr', 'info']) ids.push('fq_' + where + '_' + key);
    const row = ATIS_ROW[slot];
    if (row !== undefined) ids.push('atis' + row + '_station');
  }
  for (const n of CP_ROWS) ids.push('cp' + n + '_freq');
  for (const j of LEG_NUMBERS) ids.push(legField(j, 'remark'));
  return ids;
})();

/** What `geoValues` needs: one feature per waypoint (or null), plus the three aerodromes. */
export interface GeoSources {
  /** aligned with the plan's waypoint list; `null` where nothing is known */
  points: readonly (GeoFeature | null)[];
  slots: Readonly<Partial<Record<AipSlot, GeoFeature | null>>>;
}

/**
 * The whole feed, as one map over {@link GEO_IDS}.
 *
 * Every id of the domain is present — with `''` where this route has nothing to
 * say — so the store can clear what the source no longer knows in the same pass
 * it fills what it does.
 *
 * The route block follows the form's own layout (see `PlanForm`'s route table):
 * `cp1` is the departure and `cp(j+1)` the checkpoint at the END of leg `j`, so
 * waypoint `i` writes `cp(i+1)_freq`, and its remark goes into `leg<i>_remark` —
 * which is why the departure gets a frequency but no remark: the Start row of the
 * paper form has no Bemerkungen cell.
 */
export function geoValues(src: GeoSources, t: T): Map<string, string> {
  const out = new Map<string, string>();
  for (const id of GEO_IDS) out.set(id, '');

  for (const slot of AIP_SLOTS) {
    const f = src.slots[slot];
    if (!f) continue;
    for (const [id, v] of airportValues(f, slot)) out.set(id, v);
  }
  // A there-and-back flight has von === nach. The V2 and V7 blocks have a column
  // each and legitimately repeat themselves, but B6 has exactly TWO rows for the
  // whole plan — writing the same aerodrome into both would spend the second on a
  // station the pilot can already read on the first, and leave the alternate's
  // weather nowhere to go.
  const dep = src.slots.p1;
  const dest = src.slots.p2f;
  if (dep && dest && dest.id === dep.id) out.set('atis2_station', '');

  src.points.forEach((f, i) => {
    const line = checkpointLine(f, t);
    const cp = i + 1;
    if (cp <= MAX_ROUTE_LEGS + 1 && line.freq) out.set('cp' + cp + '_freq', line.freq);
    if (i >= 1 && i <= MAX_ROUTE_LEGS && line.remark) out.set('leg' + i + '_remark', line.remark);
  });

  return out;
}

/* ================================================================== *
 * --- the network step ---
 * ================================================================== */

/** ICAO location indicator — what `von` / `nach` / `alt_name` may be looked up by. */
const ICAO_RE = /^[A-Z]{4}$/;

/** layer/id → the feature, or `null` for "openAIP does not have this". */
const cache = new Map<string, GeoFeature | null>();
/** in-flight lookups, so twelve waypoints on one airport cost one request */
const inflight = new Map<string, Promise<GeoFeature | null>>();
/** ICAO code → cache key, filled from every airport that passes through */
const byIcao = new Map<string, string>();
/** ICAO codes the search has already failed to resolve */
const noIcao = new Set<string>();

const cacheKey = (layer: string, id: string): string => layer + '/' + id;

/**
 * Remember a feature somebody else already fetched — the info card does this
 * before setting an aerodrome as the alternate, so the V7 row fills with no
 * second round trip.
 */
export function cacheFeature(f: GeoFeature): void {
  if (!f.id) return;
  cache.set(cacheKey(f.layer, f.id), f);
  if (f.layer === 'airports') {
    const icao = (isObj(f.props) ? str(f.props['icao']) : '').toUpperCase();
    if (ICAO_RE.test(icao)) {
      byIcao.set(icao, cacheKey(f.layer, f.id));
      noIcao.delete(icao);
    }
  }
}

/**
 * Forget what ONE plan cached, so the next resolve really asks again.
 *
 * The ⟳ beside „Platzdaten" needs it: every lookup this feed makes is answered
 * from the map above for the lifetime of the tab, so without this the control
 * re-applied cached values and reported „gerade aktualisiert" without a single
 * request. Only this plan's entries go — another tab's route, and the map's own
 * info cards, keep theirs.
 *
 * `inflight` is deliberately left alone: a lookup that is already out is by
 * definition fresh, and deleting its key would let its `finally` remove a newer
 * one.
 */
export function forgetPlanGeo(
  waypoints: readonly Waypoint[],
  icaos: Readonly<Partial<Record<AipSlot, string>>>,
): void {
  for (const w of waypoints) if (w.ref) cache.delete(cacheKey(w.ref.layer, w.ref.id));
  for (const slot of AIP_SLOTS) {
    const icao = (icaos[slot] ?? '').trim().toUpperCase();
    if (!ICAO_RE.test(icao)) continue;
    noIcao.delete(icao);
    const known = byIcao.get(icao);
    if (known !== undefined) {
      cache.delete(known);
      byIcao.delete(icao);
    }
  }
}

/** Test seam: forget everything (the cache is module state, and vitest is not). */
export function clearGeoCache(): void {
  cache.clear();
  inflight.clear();
  byIcao.clear();
  noIcao.clear();
}

/**
 * One feature, at most once.
 *
 * A **404 is cached** — that id will not appear tomorrow either — but any other
 * failure is not: a lost connection must not permanently blank the V7 block for
 * this browser tab.
 */
async function featureFor(layer: FeatureLayer, id: string): Promise<GeoFeature | null> {
  const key = cacheKey(layer, id);
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  const live = inflight.get(key);
  if (live) return live;
  const p = api
    .getFeature(layer, id, false)
    .then(f => {
      cacheFeature(f);
      return f;
    })
    .catch((e: unknown) => {
      if (e instanceof api.ApiError && e.status === 404) cache.set(key, null);
      return null;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

/** The aerodrome behind an ICAO code the pilot typed (`nach`, `alt_name`). */
async function airportByIcao(icao: string): Promise<GeoFeature | null> {
  if (!ICAO_RE.test(icao) || noIcao.has(icao)) return null;
  const known = byIcao.get(icao);
  if (known !== undefined) return cache.get(known) ?? null;
  let hits: api.GeoHit[];
  try {
    hits = await api.searchGeo(icao, { limit: 8 });
  } catch {
    return null;
  }
  const hit = hits.find(h => h.layer === 'airports' && (h.ident ?? '').toUpperCase() === icao);
  if (!hit) {
    noIcao.add(icao);
    return null;
  }
  return featureFor('airports', hit.id);
}

/**
 * Where an aerodrome is, for a caller that only has its code.
 *
 * It exists for the nearby-METAR fallback (`wx.ts`): asking "which station near LJCE
 * reports?" needs LJCE's coordinates, and this feed has usually resolved them already
 * — `von` / `nach` / `alt_name` are exactly what it looks up, and every waypoint's own
 * feature fills the same ICAO index. So the common case is a cache read and no request
 * at all, and the module that owns the openAIP cache stays the only one that touches it.
 *
 * `null` when openAIP has no such aerodrome; the caller has its own fallback for that.
 */
export async function airportLatLon(icao: string): Promise<{ lat: number; lon: number } | null> {
  const f = await airportByIcao(icao.trim().toUpperCase());
  if (!f || !Number.isFinite(f.lat) || !Number.isFinite(f.lon)) return null;
  return { lat: f.lat, lon: f.lon };
}

/**
 * Resolve everything one plan needs: the feature behind each snapped waypoint,
 * and the aerodrome behind each of the three ICAO cells. A waypoint that IS the
 * departure aerodrome is looked up once — the ICAO index is filled from the
 * waypoint's own feature before the search would run.
 */
export async function resolveGeo(
  waypoints: readonly Waypoint[],
  icaos: Readonly<Partial<Record<AipSlot, string>>>,
): Promise<GeoSources> {
  const points = await Promise.all(
    waypoints.map(w => (w.ref ? featureFor(w.ref.layer, w.ref.id) : Promise.resolve(null))),
  );
  const slots: Partial<Record<AipSlot, GeoFeature | null>> = {};
  for (const slot of AIP_SLOTS) {
    const icao = (icaos[slot] ?? '').trim().toUpperCase();
    if (!ICAO_RE.test(icao)) continue;
    slots[slot] = await airportByIcao(icao);
  }
  return { points, slots };
}

/** The three ICAO cells the V7 / V2 blocks are about, in slot order. */
export function planIcaos(store: PlanStore): Record<AipSlot, string> {
  return {
    p1: store.val('von').toUpperCase(),
    p2f: store.val('nach').toUpperCase(),
    p20: store.val('alt_name').toUpperCase(),
  };
}

/**
 * What the enrichment depends on, as one comparable string: the snapped waypoints
 * in order, the three aerodrome cells, and the language the remarks are written
 * in. Nothing the feed itself writes is in here — that is what keeps it from
 * feeding on its own output.
 */
export function geoSignature(store: PlanStore, lang: string): string {
  const wps = (store.route?.waypoints ?? []).map(w => (w.ref ? w.ref.layer + '/' + w.ref.id : '-')).join(',');
  const icaos = planIcaos(store);
  // '\u0001' and not '': with an empty separator `von='LOWK', nach=''` and
  // `von='', nach='LOWK'` would be the same key. Written as an escape, not as a
  // raw control character, so it is visible to a reader (`FIELD_SEP` does the same).
  return [lang, wps, icaos.p1, icaos.p2f, icaos.p20].join('\u0001');
}

/**
 * Fetch what the plan needs and hand the values to the store — the one impure
 * function here, and the only one that knows about `PlanStore`.
 *
 * It writes ONCE, after every lookup has settled: a partial pass would clear the
 * V7 block and refill it a moment later, which is two autosaves and a visible
 * flicker for no information gained.
 */
export async function enrichPlan(
  store: PlanStore,
  t: T,
  opts: { force?: boolean } = {},
): Promise<void> {
  const waypoints = store.route?.waypoints ?? [];
  const icaos = planIcaos(store);
  // the ⟳: without this every lookup is answered from the tab's own cache and
  // the control refreshes nothing
  if (opts.force) forgetPlanGeo(waypoints, icaos);
  const before = geoSignature(store, '');
  const src = await resolveGeo(waypoints, icaos);
  // The route may have moved on while the lookups were out — a slow answer must not
  // write yesterday's frequencies over today's route. The caller re-runs on every
  // signature change, so the newer pass is already on its way.
  if (geoSignature(store, '') !== before) return;
  store.setGeo(geoValues(src, t));
}
